import React, { useEffect, useMemo } from 'react';
import { RiArrowRightLine } from '@remixicon/react';

import StatCard from './StatCard';
import QuickActions from './QuickActions';
import ActivityPanel from './ActivityPanel';
import SyncChart from './SyncChart';

import {SyncTable} from '../Sync/SyncOverview';
import { getSyncData } from '../../hooks/data';
import { fetchConnectionsCount, fetchRunsCount, fetchCompletedRunsCount } from '../../services/ApiService';
import { useUser } from '../../context/UserContext';
import toast from 'react-hot-toast';
import LoadingState from '../../components/LoadingState';



// import SyncHistory from './SyncHistory';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- no props yet; keeps the props type in place for callers
interface DashboardProps {}

interface StatDefinition {
  label: string;
  fetcher: () => Promise<number>;
}

const COUNT_UP_DURATION_MS = 900;

const StatMetric: React.FC<{ definition: StatDefinition; ready: boolean; onLoaded: () => void }> = ({
  definition,
  ready,
  onLoaded,
}) => {
  const [value, setValue] = React.useState<number | null>(null);
  const [displayValue, setDisplayValue] = React.useState(0);
  const hasReportedRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await definition.fetcher();
        if (!cancelled) {
          setValue(result);
        }
      } catch (error) {
        console.error(`Failed to load statistic ${definition.label}`, error);
        if (!cancelled) {
          setValue(null);
        }
      } finally {
        if (!cancelled && !hasReportedRef.current) {
          hasReportedRef.current = true;
          onLoaded();
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready || value === null) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setDisplayValue(value);
      return;
    }

    const startTime = performance.now();
    let frameId: number;

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / COUNT_UP_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * eased));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [ready, value]);

  return <StatCard title={definition.label} value={displayValue} />;
};

const Dashboard: React.FC<DashboardProps> = () => {
  const { user } = useUser();
  const statistics: StatDefinition[] = useMemo(
    () => [
    { label: 'Koppelingen', fetcher: fetchConnectionsCount },
    { label: 'Totale synchronisaties', fetcher: fetchRunsCount },
    { label: 'Geslaagde synchronisaties', fetcher: fetchCompletedRunsCount }
  ], []);

  const [statsLoadedCount, setStatsLoadedCount] = React.useState(0);
  const [activityLoaded, setActivityLoaded] = React.useState(false);
  const handleStatLoaded = React.useCallback(() => setStatsLoadedCount((count) => count + 1), []);
  const handleActivityLoaded = React.useCallback(() => setActivityLoaded(true), []);

  if (!user?.administrationCodes?.length) {
    toast.error("Geen administratie beschikbaar.");
    return <></>;
  }
  const { data: syncdata, loading: syncDataLoading } = getSyncData(5);

  // Wait for every fetch on this page before revealing any of them, so panels
  // don't pop in one by one as their individual requests resolve.
  const allReady = statsLoadedCount >= statistics.length && activityLoaded && !syncDataLoading;

  return (
    <>
      <div className="flex flex-col gap-10">
        {/*  max-w-[904px] */}
        <h1 className="text-xl font-semibold text-gray-900">Overzicht</h1>

        <div className="flex flex-wrap gap-4 items-start w-full rounded-2xl max-md:max-w-full">
          {statistics.map((definition) => (
            <StatMetric
              key={definition.label}
              definition={definition}
              ready={allReady}
              onLoaded={handleStatLoaded}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-5 stretch w-full max-md:max-w-full">
          <QuickActions />
          <ActivityPanel ready={allReady} onLoaded={handleActivityLoaded} />
          <SyncChart syncData={syncdata} loading={!allReady} />
        </div>
        {/* Recent syncs card */}
        <div className="flex flex-col w-full bg-white rounded-2xl shadow-md p-4 gap-4 mt-6">
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-gray-900">Recente synchronisaties</span>
            <a
              href="/synchronisaties"
              className="flex gap-2 items-center px-4 py-2.5 rounded-lg border border-gray-300 hover:bg-secondary_shadow bg-secondary text-white text-sm"
            >
              <RiArrowRightLine className="w-5 h-5" />
              <span>Alle synchronisaties</span>
            </a>
          </div>
          {!allReady ? <LoadingState /> : <SyncTable syncData={syncdata} />}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
